import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";

import { useTableAllocationStore } from "@/data/stores/tableAllocation";

import type { IdentityChangedEventData } from "@/data/events/events";
import type { IProfile } from "@/data/model/Profile";
import { role } from "@/data/util/Config";

import { objectToArray } from "@/shared/utils/mappers";

export const useIdentityStore = defineStore("identity", () => {
    const allocation = useTableAllocationStore();

    // state
    const profile = ref<IProfile | undefined>(undefined);

    // getters
    const identityId = computed(() => profile.value?.identityId);
    const roles = computed(() => objectToArray(profile.value?.roles));
    const operatorLocations = computed(() => objectToArray(profile.value?.operatorLocations));
    const isAdmin = computed(() => roles.value.includes(role.Admin));
    const isWaiterInCurrentLocation = computed(
        () => allocation.locationId !== undefined && isWaiterInLocation(allocation.locationId)
    );
    const isAuthenticated = computed<boolean>(() => profile.value?.authenticatedAs != null)
    
    const hasOperatorAccess = computed(() => isAdmin.value || isWaiterInCurrentLocation.value);

    // actions
    function onIdentityChanged(eventData: IdentityChangedEventData) {
        const previousIdentityId = profile.value?.identityId;
        profile.value = eventData.profile;

        // a login or logout keeps the allocation, so the ordered articles are not asked for again on their own. a
        // profile change of the same identity leaves them as they are
        if (previousIdentityId !== eventData.profile?.identityId) {
            allocation.reloadOrderedArticlesForNewIdentity();
        }
    }

    function setIdentity(profileInfo: IProfile) {
        profile.value = profileInfo;
    }

    function isWaiterInLocation(locationId: string): boolean {
        return roles.value.includes(role.Operator) && operatorLocations.value.includes(locationId);
    }

    return {
        // state
        identityId,
        roles,
        operatorLocations,
        profile,

        // getters
        isAdmin,
        isWaiterInCurrentLocation,
        hasOperatorAccess,
        isAuthenticated,

        // actions
        setIdentity,
        onIdentityChanged,
    };
});

export type IdentityStore = ReturnType<typeof useIdentityStore>;

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useIdentityStore, import.meta.hot));
}
